import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { DollarSign, Plus, X, Download } from "lucide-react";

const money = (n) => (n < 0 ? "-$" : "$") + Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const RATE_FIELDS = [["rate_new_business", "New Business %"], ["rate_renewal", "Renewal %"], ["rate_self_generated", "Self-Generated %"], ["rate_agency_generated", "Agency-Generated %"], ["rate_website", "Website Lead %"], ["rate_apollo", "Apollo / Intelligence %"], ["rate_referral", "Referral %"], ["rate_cross_sell", "Cross-Sell %"]];

function SummaryCards({ s }) {
  if (!s) return null;
  const cards = [["This Month", s.this_month], ["Year to Date", s.ytd], ["Unpaid", s.unpaid], ["Total", s.total]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="commission-summary">
      {cards.map(([l, v]) => (
        <div key={l} className="bg-white border border-border rounded-sm p-4">
          <div className="text-xs uppercase text-mutedsea tracking-wide">{l}</div>
          <div className={`font-serif text-2xl mt-1 ${v < 0 ? "text-seared" : "text-navy"}`}>{money(v)}</div>
        </div>
      ))}
    </div>
  );
}

function LedgerTable({ items, admin, onAction, selected, setSelected }) {
  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
      <table className="w-full text-sm">
        <thead><tr className="bg-cloud border-b border-border">
          {admin && <th className="px-2 py-2"></th>}
          {["Date", admin ? "Producer" : null, "Type", "Client/Policy", "Carrier / LOB", "Basis", "Rate", "Commission", "Status", "Payment"].filter(Boolean).map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold whitespace-nowrap">{h}</th>)}
          {admin && <th className="px-3 py-2 text-xs uppercase text-navy font-semibold">Actions</th>}
        </tr></thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} data-testid={`ledger-${t.id}`} className="border-b border-border last:border-0 hover:bg-ice">
              {admin && <td className="px-2 py-2"><input type="checkbox" data-testid={`ledger-select-${t.id}`} checked={!!selected[t.id]} onChange={(e) => setSelected((s) => ({ ...s, [t.id]: e.target.checked }))} /></td>}
              <td className="px-3 py-2 text-xs whitespace-nowrap">{(t.effective_date || t.created_at || "").slice(0, 10)}</td>
              {admin && <td className="px-3 py-2 text-xs">{t.producer_name}</td>}
              <td className="px-3 py-2"><Badge variant="outline" className="bg-ice text-atlantic border-sky/50 text-xs">{t.transaction_type}</Badge></td>
              <td className="px-3 py-2 text-xs text-mutedsea">{t.policy_id ? t.policy_id.slice(0, 8) : "—"}</td>
              <td className="px-3 py-2 text-xs text-mutedsea">{t.carrier || "—"}{t.line_of_business ? ` / ${t.line_of_business}` : ""}</td>
              <td className="px-3 py-2 text-xs">{t.commission_basis}{t.basis_amount ? ` (${money(t.basis_amount)})` : ""}</td>
              <td className="px-3 py-2 text-xs">{t.producer_rate ? `${t.producer_rate}%` : "—"}</td>
              <td className={`px-3 py-2 font-medium ${t.producer_commission < 0 ? "text-seared" : "text-slate"}`}>{money(t.producer_commission)}</td>
              <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{t.status}</Badge></td>
              <td className="px-3 py-2 text-xs">{t.payment_status === "Paid" ? <span className="text-seagreen">Paid {t.payment_reference ? `· ${t.payment_reference}` : ""}</span> : <span className="text-mutedsea">Unpaid</span>}</td>
              {admin && <td className="px-3 py-2">
                <div className="flex gap-1 flex-wrap">
                  {t.commission_basis !== "actual" && t.transaction_type === "New Business" && <button data-testid={`actual-${t.id}`} onClick={() => onAction("actual", t)} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Enter Actual</button>}
                  {t.status !== "Approved" && <button data-testid={`approve-${t.id}`} onClick={() => onAction("approve", t)} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Approve</button>}
                </div>
              </td>}
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-mutedsea text-sm">No commission transactions.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AgencyLedger() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ producer_user_id: "", transaction_type: "", payment_status: "", period: "" });
  const [selected, setSelected] = useState({});
  const load = useCallback(async () => {
    const qs = Object.entries(filter).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    try { const { data } = await api.get(`/commissions/ledger?${qs}`); setItems(data.items); } catch { toast.error("Could not load ledger."); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/auth/users").then(({ data }) => setUsers(data.items)).catch(() => {}); }, []);

  const onAction = async (kind, t) => {
    try {
      if (kind === "actual") {
        const v = window.prompt("Actual agency commission received ($):");
        if (!v) return;
        const { data } = await api.post(`/commissions/ledger/${t.id}/actual`, { actual_agency_commission: parseFloat(v) });
        toast.success(`Reconciled. Adjustment ${money(data.adjustment)}.`);
      } else if (kind === "approve") { await api.post(`/commissions/ledger/${t.id}/status`, { status: "Approved" }); toast.success("Approved."); }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markPaid = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Select transactions first.");
    const ref = window.prompt("Payment reference (e.g. check #):") || "";
    try { const { data } = await api.post("/commissions/ledger/mark-paid", { ids, payment_reference: ref }); toast.success(`Marked ${data.paid} paid.`); setSelected({}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportCsv = () => { window.open(`${api.defaults.baseURL}/commissions/ledger/export${filter.producer_user_id ? `?producer_user_id=${filter.producer_user_id}` : ""}`, "_blank"); };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <select data-testid="filter-producer" value={filter.producer_user_id} onChange={(e) => setFilter({ ...filter, producer_user_id: e.target.value })} className="h-9 border border-border rounded-sm px-2 text-sm"><option value="">All producers</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
        <select data-testid="filter-type" value={filter.transaction_type} onChange={(e) => setFilter({ ...filter, transaction_type: e.target.value })} className="h-9 border border-border rounded-sm px-2 text-sm"><option value="">All types</option>{["New Business", "Renewal", "Endorsement", "Cancellation", "Manual Adjustment", "Bonus"].map((t) => <option key={t}>{t}</option>)}</select>
        <select data-testid="filter-payment" value={filter.payment_status} onChange={(e) => setFilter({ ...filter, payment_status: e.target.value })} className="h-9 border border-border rounded-sm px-2 text-sm"><option value="">All payments</option><option>Unpaid</option><option>Paid</option></select>
        <Input data-testid="filter-period" placeholder="YYYY or YYYY-MM" value={filter.period} onChange={(e) => setFilter({ ...filter, period: e.target.value })} className="w-36 h-9" />
        <div className="flex-1" />
        <Button data-testid="mark-paid-btn" variant="outline" onClick={markPaid}>Mark Paid</Button>
        <Button data-testid="export-csv" variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" />Export</Button>
      </div>
      <LedgerTable items={items} admin onAction={onAction} selected={selected} setSelected={setSelected} />
    </div>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null);
  const load = async () => { try { const { data } = await api.get("/commissions/plans"); setPlans(data.items); } catch { toast.error("Could not load plans."); } };
  useEffect(() => { load(); api.get("/auth/users").then(({ data }) => setUsers(data.items.filter((u) => ["producer", "admin", "super_admin", "account_manager"].includes(u.role)))).catch(() => {}); }, []);
  return (
    <div>
      <div className="flex justify-end mb-3"><Button data-testid="create-plan" onClick={() => setDialog({ overrides: [] })} className="bg-navy text-white"><Plus className="h-4 w-4 mr-1.5" />New Plan</Button></div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Producer", "Effective", "New Biz", "Renewal", "Overrides", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>{plans.map((p) => (
            <tr key={p.id} data-testid={`plan-${p.id}`} className="border-b border-border last:border-0 hover:bg-ice">
              <td className="px-3 py-2 text-slate">{p.producer_name}</td>
              <td className="px-3 py-2 text-xs">{p.effective_date}{p.is_future && <Badge className="ml-1 bg-amber-100 text-amber-700 border-amber-300 text-xs">Future</Badge>}</td>
              <td className="px-3 py-2">{p.rate_new_business}%</td><td className="px-3 py-2">{p.rate_renewal}%</td>
              <td className="px-3 py-2 text-xs text-mutedsea">{(p.overrides || []).length} rule(s)</td>
              <td className="px-3 py-2">{p.active === false ? <Badge variant="outline" className="bg-slate-100 text-slate-500">Inactive</Badge> : <Badge variant="outline" className="bg-seagreen/10 text-seagreen border-seagreen/30">Active</Badge>}</td>
              <td className="px-3 py-2">{p.is_future && <button data-testid={`edit-plan-${p.id}`} onClick={() => setDialog(p)} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Edit</button>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      {dialog && <PlanDialog plan={dialog} users={users} onClose={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function PlanDialog({ plan, users, onClose }) {
  const isEdit = !!plan.id;
  const [f, setF] = useState({ producer_user_id: plan.producer_user_id || "", effective_date: plan.effective_date || new Date().toISOString().slice(0, 10), overrides: plan.overrides || [], ...RATE_FIELDS.reduce((a, [k]) => ({ ...a, [k]: plan[k] ?? "" }), {}) });
  const addOverride = () => setF({ ...f, overrides: [...f.overrides, { type: "carrier", key: "", rate: "" }] });
  const submit = async () => {
    try {
      if (isEdit) await api.put(`/commissions/plans/${plan.id}`, f);
      else await api.post("/commissions/plans", f);
      toast.success("Plan saved."); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="plan-dialog">
      <div className="bg-white rounded-sm border border-border w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex justify-between items-center px-5 py-3 border-b border-border"><h3 className="font-serif text-lg text-navy">{isEdit ? "Edit Future Plan" : "New Commission Plan"}</h3><button onClick={onClose}><X className="h-4 w-4 text-mutedsea" /></button></div>
        <div className="p-5">
          {!isEdit && <><label className="text-sm">Producer</label><select data-testid="plan-producer" value={f.producer_user_id} onChange={(e) => setF({ ...f, producer_user_id: e.target.value })} className="w-full h-9 border border-border rounded-sm px-2 text-sm mb-3"><option value="">Select…</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}</select></>}
          <label className="text-sm">Effective date</label><Input type="date" data-testid="plan-effective" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} className="mb-3" />
          <div className="grid grid-cols-2 gap-3 mb-4">
            {RATE_FIELDS.map(([k, lbl]) => <div key={k}><label className="text-xs text-mutedsea">{lbl}</label><Input data-testid={`plan-${k}`} type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>)}
          </div>
          <div className="flex justify-between items-center mb-2"><span className="text-sm font-medium text-navy">Overrides (carrier / line / product)</span><button data-testid="add-override" onClick={addOverride} className="text-xs px-2 py-1 border border-border rounded-sm">+ Override</button></div>
          {f.overrides.map((o, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <select value={o.type} onChange={(e) => { const ov = [...f.overrides]; ov[i].type = e.target.value; setF({ ...f, overrides: ov }); }} className="h-9 border border-border rounded-sm px-2 text-sm"><option value="carrier">Carrier</option><option value="line">Line</option><option value="product">Product</option></select>
              <Input placeholder="Key (e.g. carrier name)" value={o.key} onChange={(e) => { const ov = [...f.overrides]; ov[i].key = e.target.value; setF({ ...f, overrides: ov }); }} />
              <Input placeholder="%" type="number" value={o.rate} onChange={(e) => { const ov = [...f.overrides]; ov[i].rate = e.target.value; setF({ ...f, overrides: ov }); }} className="w-24" />
              <button onClick={() => setF({ ...f, overrides: f.overrides.filter((_, j) => j !== i) })}><X className="h-4 w-4 text-mutedsea" /></button>
            </div>
          ))}
          <Button data-testid="plan-submit" onClick={submit} className="w-full mt-4 bg-navy text-white">Save Plan</Button>
        </div>
      </div>
    </div>
  );
}

function MyCommissions() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [sc, setSc] = useState(null);
  const [usage, setUsage] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => {
    api.get("/commissions/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/commissions/ledger").then(({ data }) => setItems(data.items)).catch(() => {});
    api.get("/management/scorecard").then(({ data }) => setSc(data)).catch(() => {});
    api.get("/management/apollo/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  const dl = (fmt) => window.open(`${api.defaults.baseURL}/commissions/statement.${fmt}?month=${month}`, "_blank");
  return (
    <div data-testid="my-commissions">
      <SummaryCards s={summary} />
      {sc && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-5" data-testid="my-scorecard">
          {[["Premium YTD", money(sc.written_premium_ytd)], ["New Business", sc.new_business_ytd], ["Renewals", sc.renewals_ytd], ["Open Pipeline", money(sc.pipeline_value)], ["Close Rate", `${sc.close_rate}%`], ["Tasks Due", sc.tasks_due]].map(([l, v]) => (
            <div key={l} className="bg-white border border-border rounded-sm p-3"><div className="text-xs uppercase text-mutedsea">{l}</div><div className="font-serif text-xl text-navy mt-0.5">{v}</div></div>
          ))}
        </div>
      )}
      {usage && (
        <div className="bg-ice border border-sky/40 rounded-sm p-3 mb-5 text-sm text-atlantic" data-testid="apollo-usage-tile">
          Intelligence usage today: {usage.used_today.reveals} reveals, {usage.used_today.credits} credits
          {usage.remaining.daily_reveals != null ? ` · ${usage.remaining.daily_reveals} reveals remaining today` : " · reveals unlimited"}
          {usage.remaining.monthly_credits != null ? ` · ${usage.remaining.monthly_credits} credits remaining this month` : " · credits unlimited"}
        </div>
      )}
      <div className="flex items-end gap-2 mb-4 bg-cloud border border-border rounded-sm p-3">
        <div><label className="text-xs text-navy block">Statement month</label><Input type="month" data-testid="statement-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 h-9" /></div>
        <Button data-testid="statement-pdf" variant="outline" onClick={() => dl("pdf")}><Download className="h-4 w-4 mr-1.5" />PDF Statement</Button>
        <Button data-testid="statement-csv" variant="outline" onClick={() => dl("csv")}><Download className="h-4 w-4 mr-1.5" />CSV</Button>
      </div>
      {summary && <div className="flex flex-wrap gap-2 mb-4 text-xs">{Object.entries(summary.by_type || {}).map(([k, v]) => <Badge key={k} variant="outline" className="bg-ice text-atlantic border-sky/50">{k}: {money(v)}</Badge>)}</div>}
      <LedgerTable items={items} admin={false} selected={{}} setSelected={() => {}} onAction={() => {}} />
    </div>
  );
}

export default function Commissions() {
  const { level } = useAuth();
  const isAdmin = level >= 80;
  return (
    <div className="p-6" data-testid="commissions-page">
      <div className="flex items-center gap-2 mb-1"><DollarSign className="h-6 w-6 text-navy" /><h1 className="font-serif text-2xl text-navy">Commissions</h1></div>
      <p className="text-sm text-mutedsea mb-5">{isAdmin ? "Agency-wide commission plans, ledger, reconciliation and payments." : "Your commission earnings — read directly from the agency ledger."}</p>
      {!isAdmin ? <MyCommissions /> : (
        <Tabs defaultValue="ledger">
          <TabsList className="bg-cloud">
            <TabsTrigger value="ledger" data-testid="tab-ledger" className="data-[state=active]:bg-navy data-[state=active]:text-white">Ledger</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="data-[state=active]:bg-navy data-[state=active]:text-white">Commission Plans</TabsTrigger>
          </TabsList>
          <TabsContent value="ledger" className="mt-4"><AgencyLedger /></TabsContent>
          <TabsContent value="plans" className="mt-4"><PlansTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
